import { useState } from 'react';
import type { FunnelPageItem, FunnelPageProductInfo } from '../../../types';
import type { PublishFunnelPagePayload } from '../../../hooks/useFunnelPublishPage';
import { EditableFunnelPageCard } from './EditableFunnelPageCard';
import { LegacyFunnelPageCard } from './LegacyFunnelPageCard';

interface FunnelPageEditorProps {
    page: FunnelPageItem;
    stepNumber: number;
    storeId: number;
    isDraft: boolean;
    isLegacy: boolean;
    isPublishing: boolean;
    isDeleting: boolean;
    isHiding: boolean;
    isDetaching: boolean;
    isStepUnlocked: boolean;
    allPages: FunnelPageItem[];
    productInfoByPageId: Record<number, FunnelPageProductInfo | undefined>;
    onPublish: (payload: PublishFunnelPagePayload) => void;
    onDelete: (pageId: number) => void;
    onHide: (pageId: number) => void;
    onDetachFromParent: (pageId: number) => void;
}

/**
 * Thin dispatcher: legacy pages get the read-only card (with a "Replace
 * With Existing Product" escape hatch that swaps to the editable card);
 * everything else renders the editable card directly.
 */
export const FunnelPageEditor = ({
    page,
    stepNumber,
    storeId,
    isDraft,
    isLegacy,
    isPublishing,
    isDeleting,
    isHiding,
    isDetaching,
    isStepUnlocked,
    allPages,
    productInfoByPageId,
    onPublish,
    onDelete,
    onHide,
    onDetachFromParent,
}: FunnelPageEditorProps) => {
    const [replaceMode, setReplaceMode] = useState(false);

    if (isLegacy && !replaceMode) {
        return (
            <LegacyFunnelPageCard
                page={page}
                stepNumber={stepNumber}
                storeId={storeId}
                isDeleting={isDeleting}
                onDelete={onDelete}
                onReplace={() => setReplaceMode(true)}
            />
        );
    }

    return (
        <EditableFunnelPageCard
            page={page}
            stepNumber={stepNumber}
            isDraft={isDraft}
            isPublishing={isPublishing}
            isDeleting={isDeleting}
            isHiding={isHiding}
            isDetaching={isDetaching}
            isStepUnlocked={isStepUnlocked}
            allPages={allPages}
            productInfoByPageId={productInfoByPageId}
            onPublish={onPublish}
            onDelete={onDelete}
            onHide={onHide}
            onDetachFromParent={onDetachFromParent}
        />
    );
};
